/**
 * UI language, and the direction that follows from it.
 *
 * Two languages that are **not the same axis**, and conflating them is the
 * main way a bilingual app of this kind goes wrong:
 *
 *   UI language      what the chrome is in. The user's choice.
 *   Content language what a document is in. A property of the data.
 *
 * This provider owns the first. The second is carried per element from the
 * data's own `lang`, by `<Raw>` in components/ui.
 */
import {
  createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode,
} from 'react'
import { en, type Key } from './en.ts'
import { ar } from './ar.ts'
import { VOCABS, type VocabName } from './vocab.ts'
import type { Locale } from './format.ts'

export type { Locale }

const KEY = 'explorer.locale'
const DICTS = { en, ar } as const

type Vars = Record<string, string | number>

type I18nContext = {
  locale: Locale
  dir: 'ltr' | 'rtl'
  setLocale: (l: Locale) => void
  toggleLocale: () => void
  /** A chrome string. `{name}` placeholders are filled from `vars`. */
  t: (key: Key, vars?: Vars) => string
  /**
   * A chrome string in the plural form `n` calls for. Falls back to the base
   * key, so a dictionary that does not carry the category still renders.
   */
  tn: (key: Key, n: number, vars?: Vars) => string
  /**
   * A database value -- a page type, a statement type, an enum. An unknown
   * key returns null, and the caller renders the raw value in a muted
   * monospace style rather than nothing (§11.2).
   */
  v: (vocab: VocabName, value: string | null | undefined) => string | null
  /** The same, but falling back to the raw value. */
  vf: (vocab: VocabName, value: string | null | undefined) => string
}

const Ctx = createContext<I18nContext | null>(null)

function read(): Locale {
  try {
    const v = localStorage.getItem(KEY)
    if (v === 'en' || v === 'ar') return v
  } catch {}
  // The boot script in index.html already made this decision for the first
  // paint; re-deriving it the same way keeps the two in agreement.
  const l = document.documentElement.lang
  if (l === 'ar' || l === 'en') return l
  return 'en'
}

function interpolate(s: string, vars?: Vars): string {
  if (!vars) return s
  return s.replace(/\{(\w+)\}/g, (m, k: string) =>
    k in vars ? String(vars[k]) : m,
  )
}

/**
 * Which plural form `n` takes, per CLDR.
 *
 * English returns only `one` and `other`, so this is exactly the old
 * `n === 1` test there. Arabic returns six categories, and the dictionary
 * answers as many of them as it carries.
 */
const pluralRules = new Map<Locale, Intl.PluralRules>()
function pluralCategory(locale: Locale, n: number): string {
  let rules = pluralRules.get(locale)
  if (!rules) {
    rules = new Intl.PluralRules(locale)
    pluralRules.set(locale, rules)
  }
  return rules.select(n)
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(read)
  const dir = locale === 'ar' ? 'rtl' : 'ltr'

  useEffect(() => {
    const root = document.documentElement
    root.lang = locale
    root.dir = dir
    try { localStorage.setItem(KEY, locale) } catch {}
  }, [locale, dir])

  const setLocale = useCallback((l: Locale) => setLocaleState(l), [])
  const toggleLocale = useCallback(
    () => setLocaleState((l) => (l === 'en' ? 'ar' : 'en')),
    [],
  )

  const value = useMemo<I18nContext>(() => {
    const dict = DICTS[locale]
    const t = (key: Key, vars?: Vars) => interpolate(dict[key] ?? en[key] ?? key, vars)
    // The plural variants are optional per language, so the lookup is by
    // string rather than by `Key` -- `common.documents_few` exists in Arabic
    // and in no other dictionary.
    const forms = dict as Record<string, string | undefined>
    const tn = (key: Key, n: number, vars?: Vars) => {
      const variant = forms[`${key}_${pluralCategory(locale, n)}`]
      return interpolate(variant ?? dict[key] ?? en[key] ?? key, { n, ...vars })
    }
    const v = (vocab: VocabName, val: string | null | undefined) => {
      if (!val) return null
      return VOCABS[vocab][val]?.[locale] ?? null
    }
    return {
      locale, dir, setLocale, toggleLocale, t, tn, v,
      vf: (vocab, val) => v(vocab, val) ?? val ?? '—',
    }
  }, [locale, dir, setLocale, toggleLocale])

  return <Ctx value={value}>{children}</Ctx>
}

export function useT(): I18nContext {
  const c = use(Ctx)
  if (!c) throw new Error('useT outside I18nProvider')
  return c
}
